/**
 * /admin/blogs/new      → create a new post
 * /admin/blogs/:id/edit → edit an existing post
 *
 * Save logic: all saves go through the blog-publish Edge Function (never writes
 * blog_posts directly). This keeps validation, audit trail, and payload shape
 * identical to agency automation.
 */

import { ArrowLeft, Copy, Eye, Facebook, FileText, Instagram, Linkedin, Youtube } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import SectionRenderer from '../../components/Blog/SectionRenderer';
import { useAuthentication } from '../../hooks/useAuthentication';
import { supabase } from '../../lib/supabase';
import type { BlogPost, BlogSection, SectionType } from '../../types/blog';
import { BlogMetaForm, type MetaValues } from './blogEditor/BlogMetaForm';
import { HeroEditor, type HeroValues } from './blogEditor/HeroEditor';
import { SectionsList, type EditorSection } from './blogEditor/SectionsList';
import { StatusBar } from './blogEditor/StatusBar';
import { placeMidSubscribeCta } from '../../utils/blogSectionLayout';
import { resolveBlogImageSrc } from '../../config/blogImagePaths';

// ── Constants ────────────────────────────────────────────────────────────────

const SUPABASE_URL         = import.meta.env.VITE_SUPABASE_URL as string;
const BLOG_PUBLISH_API_KEY = import.meta.env.VITE_BLOG_PUBLISH_API_KEY as string | undefined;

const PUBLISH_FN_URL   = `${SUPABASE_URL}/functions/v1/blog-publish`;
const UPLOAD_IMAGE_URL = `${SUPABASE_URL}/functions/v1/blog-upload-image`;
const REMOTE_GENERATE_IMAGES_URL = `${SUPABASE_URL}/functions/v1/generate-blog-images`;
/** Local dev: Vite middleware writes public/images/blogs/{slug}.png. Production: Supabase edge fn. */
const GENERATE_IMAGES_URL = import.meta.env.DEV
  ? '/api/dev/generate-blog-hero'
  : REMOTE_GENERATE_IMAGES_URL;

/** Build headers for Supabase edge-function calls.
 *  Uses the signed-in user's access_token as the Authorization bearer so the
 *  Supabase gateway lets the request through without needing the anon key. */
export function edgeFnHeaders(
  bearerToken: string,
  extra: Record<string, string> = {},
): Record<string, string> {
  return {
    Authorization: `Bearer ${bearerToken}`,
    ...extra,
  };
}

/** Upload a binary image file to the blog-images bucket via the edge function.
 *  Returns the public storage URL on success, or throws on error. */
export async function uploadBlogImage(
  file: File,
  apiKey: string,
  bearerToken: string,
): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const resp = await fetch(UPLOAD_IMAGE_URL, {
    method: 'POST',
    headers: edgeFnHeaders(bearerToken, { 'x-api-key': apiKey }),
    body: form,
  });
  const result = await resp.json() as { ok?: boolean; url?: string; error?: string };
  if (!resp.ok || !result.ok || !result.url) {
    throw new Error(result.error ?? `Image upload failed (HTTP ${resp.status})`);
  }
  return result.url;
}

// ── Helper — convert DB sections to editor sections ──────────────────────────

function dbToEditorSections(dbSections: BlogSection[]): EditorSection[] {
  return dbSections.map((s) => {
    let data = s.data as Record<string, unknown>;

    // Normalise third-party text sections: `body` → `content_md` so the editor
    // always works with a single canonical key.
    if (s.type === 'text' && data.body !== undefined && data.content_md === undefined) {
      data = { ...data, content_md: data.body };
    }

    return {
      _id: s.id,
      type: s.type as SectionType,
      heading: s.heading ?? '',
      data,
    };
  });
}

// ── Helper — build publish payload ──────────────────────────────────────────

interface PublishPayload {
  slug: string;
  status: string;
  title: string;
  meta_title?: string;
  meta_description?: string;
  keywords?: string[];
  featured_image?: string;
  hero?: { headline: string; intro: string };
  sections: { type: string; heading: string; data: Record<string, unknown> }[];
  author_name?: string;
  category?: string;
  read_time_min?: number;
  scheduled_for?: string;
}

function buildPayload(
  meta: MetaValues,
  hero: HeroValues,
  sections: EditorSection[],
  status: string,
  scheduledFor: string,
): PublishPayload {
  // Auto-populate empty TOC items from sibling section headings before saving.
  const processedSections = sections.map((s) => {
    if (s.type !== 'toc') return s;
    const existingItems = (s.data.items as string[] | undefined) ?? [];
    if (existingItems.filter(Boolean).length > 0) return s; // already populated
    const headings = sections
      .filter((other) => other._id !== s._id && other.heading.trim())
      .map((other) => other.heading.trim());
    return { ...s, data: { ...s.data, items: headings } };
  });

  return {
    slug: meta.slug,
    status,
    title: meta.title,
    meta_title: meta.meta_title || undefined,
    meta_description: meta.meta_description || undefined,
    keywords: meta.keywords.length ? meta.keywords : undefined,
    featured_image: meta.featured_image || undefined,
    hero: hero.headline || hero.intro ? { headline: hero.headline, intro: hero.intro } : undefined,
    author_name: meta.author_name || undefined,
    category: meta.category || undefined,
    read_time_min: meta.read_time_min !== '' ? Number(meta.read_time_min) : undefined,
    scheduled_for: status === 'scheduled' && scheduledFor ? scheduledFor : undefined,
    sections: processedSections.map((s) => ({
      type: s.type,
      heading: s.heading,
      data: s.data,
    })),
  };
}

// ── Default state ────────────────────────────────────────────────────────────

const defaultMeta = (): MetaValues => ({
  title: '',
  slug: '',
  meta_title: '',
  meta_description: '',
  keywords: [],
  featured_image: '',
  author_name: '',
  category: '',
  read_time_min: '',
});

const defaultHero = (): HeroValues => ({ headline: '', intro: '' });

// ── Role helper (same as BlogListPage) ───────────────────────────────────────

function getJwtUserRole(
  session: { access_token: string; user: { app_metadata?: Record<string, unknown>; user_metadata?: Record<string, unknown> } } | null,
): 'admin' | 'editor' | null {
  if (!session) return null;
  const u = session.user;
  const a =
    (u.app_metadata?.user_role as string) || (u.user_metadata?.user_role as string);
  if (a === 'admin' || a === 'editor') return a;
  try {
    const part = session.access_token.split('.')[1];
    const p = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as {
      user_role?: string;
    };
    if (p.user_role === 'admin' || p.user_role === 'editor') return p.user_role;
  } catch { /* ignore */ }
  return null;
}

// ── Main page ────────────────────────────────────────────────────────────────

interface Props {
  postId?: string;   // undefined = new post
  embedded?: boolean; // true when rendered inside DashboardContainer (Layout provides topbar)
}

const BlogEditorInner: React.FC<Props> = ({ postId, embedded }) => {
  const isNew = !postId;

  const [meta, setMeta] = useState<MetaValues>(defaultMeta);
  const [hero, setHero] = useState<HeroValues>(defaultHero);
  const [sections, setSections] = useState<EditorSection[]>([]);
  const [currentStatus, setCurrentStatus] = useState<'draft' | 'scheduled' | 'published' | 'archived'>('draft');
  const [scheduledFor, setScheduledFor] = useState('');
  const [savedPostId, setSavedPostId] = useState<string | null>(null);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [mobilePane, setMobilePane] = useState<'editor' | 'preview'>('editor');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load existing post ────────────────────────────────────────────────────

  useEffect(() => {
    if (!postId) return;
    (async () => {
      setLoading(true);
      try {
        const { data: post, error: pe } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('id', postId)
          .single();
        if (pe || !post) throw pe ?? new Error('Post not found');

        const { data: dbSections } = await supabase
          .from('blog_sections')
          .select('*')
          .eq('post_id', postId)
          .order('position', { ascending: true });

        const p = post as BlogPost;
        setMeta({
          title: p.title,
          slug: p.slug,
          meta_title: p.meta_title ?? '',
          meta_description: p.meta_description ?? '',
          keywords: p.keywords ?? [],
          featured_image: p.featured_image ?? '',
          author_name: p.author_name ?? '',
          category: p.category ?? '',
          read_time_min: p.read_time_min ?? '',
        });
        setHero({ headline: p.hero_headline ?? '', intro: p.hero_intro ?? '' });
        setSections(dbToEditorSections((dbSections ?? []) as BlogSection[]));
        setCurrentStatus(p.status);
        setScheduledFor(p.scheduled_for ?? '');
        setSavedPostId(p.id);
      } catch (err) {
        setSaveError(`Failed to load post: ${(err as Error).message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [postId]);

  // ── Save (calls Edge Function) ────────────────────────────────────────────

  const handleSave = useCallback(
    async (status: 'draft' | 'scheduled' | 'published' | 'archived') => {
      setSaveError(null);
      if (!meta.slug.trim()) { setSaveError('Slug is required.'); return; }
      if (!meta.title.trim()) { setSaveError('Title is required.'); return; }
      if (sections.length === 0) { setSaveError('Add at least one section.'); return; }

      setSaving(true);
      try {
        const apiKey = BLOG_PUBLISH_API_KEY ?? '';

        const { data: { session: authSession } } = await supabase.auth.getSession();
        const bearerToken = authSession?.access_token ?? '';
        if (!bearerToken && !apiKey) throw new Error('Not authenticated — please sign in again.');

        const payload = buildPayload(meta, hero, sections, status, scheduledFor);
        const resp = await fetch(PUBLISH_FN_URL, {
          method: 'POST',
          headers: edgeFnHeaders(bearerToken, {
            'x-api-key': apiKey,
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        });

        const result = await resp.json() as { ok?: boolean; id?: string; status?: string; error?: string };
        if (!resp.ok || !result.ok) throw new Error(result.error ?? `HTTP ${resp.status}`);

        setSavedPostId(result.id ?? savedPostId);
        setCurrentStatus(status);
        showToast(
          status === 'published'
            ? '✓ Published! Live on /blog now.'
            : status === 'scheduled'
            ? '✓ Scheduled.'
            : '✓ Draft saved.',
        );
      } catch (err) {
        setSaveError((err as Error).message);
      } finally {
        setSaving(false);
      }
    },
    [meta, hero, sections, scheduledFor, savedPostId],
  );

  const handleGenerateImages = useCallback(async () => {
    setSaveError(null);
    if (!savedPostId) {
      setSaveError('Save the draft first, then generate the hero image.');
      return;
    }
    if (!meta.slug?.trim()) {
      setSaveError('Set a slug on this post before generating the hero image.');
      return;
    }

    const isLocalDev = import.meta.env.DEV;
    const apiKey = BLOG_PUBLISH_API_KEY;

    const { data: { session: authSession } } = await supabase.auth.getSession();
    const bearerToken = authSession?.access_token ?? '';
    if (!isLocalDev && !bearerToken && !apiKey) {
      setSaveError('Not authenticated — please sign in again.');
      return;
    }

    const previousFeatured = meta.featured_image;
    setGeneratingImages(true);
    try {
      const headings = sections
        .filter((s) => s.type === 'text' && s.heading)
        .map((s) => String(s.heading));

      const localBody = {
        post_id: savedPostId,
        slug: meta.slug.trim(),
        title: meta.title,
        keyword: meta.keywords?.[0] || meta.title,
        keywords: meta.keywords,
        meta_description: meta.meta_description,
        headings,
        hero_headline: hero.headline || undefined,
      };
      const remoteBody = { post_id: savedPostId };
      const remoteHeaders = edgeFnHeaders(bearerToken, {
        'x-api-key': apiKey ?? '',
        'Content-Type': 'application/json',
      });

      type HeroGenResult = {
        ok?: boolean;
        generated?: boolean;
        featured_image?: string;
        hero_path?: string;
        slug?: string;
        layoutId?: string;
        message?: string;
        error?: string;
        code?: string;
      };

      const parseHeroResponse = async (resp: Response): Promise<HeroGenResult> => {
        const text = await resp.text();
        try {
          return text.trim() ? JSON.parse(text) as HeroGenResult : {};
        } catch {
          throw new Error(text.slice(0, 200) || `HTTP ${resp.status}`);
        }
      };

      const requestHero = async (
        url: string,
        body: Record<string, unknown>,
        headers: Record<string, string>,
      ) => {
        const resp = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        const result = await parseHeroResponse(resp);
        if (!resp.ok || !result.ok) {
          throw new Error(result.error ?? `HTTP ${resp.status}`);
        }
        return result;
      };

      let result: HeroGenResult;
      let usedRemoteFallback = false;

      if (isLocalDev) {
        try {
          result = await requestHero(
            GENERATE_IMAGES_URL,
            localBody,
            { 'Content-Type': 'application/json' },
          );
        } catch (localErr) {
          const msg = (localErr as Error).message ?? String(localErr);
          const isNetworkFailure = localErr instanceof TypeError
            || /failed to fetch|network|connection refused|load failed/i.test(msg);
          if (!isNetworkFailure || (!bearerToken && !apiKey)) {
            throw localErr;
          }
          result = await requestHero(REMOTE_GENERATE_IMAGES_URL, remoteBody, remoteHeaders);
          usedRemoteFallback = true;
        }
      } else {
        if (!bearerToken && !apiKey) {
          setSaveError('Not authenticated — please sign in again.');
          return;
        }
        result = await requestHero(GENERATE_IMAGES_URL, remoteBody, remoteHeaders);
      }

      if (result.generated && result.featured_image) {
        setMeta((m) => ({ ...m, featured_image: result.featured_image! }));
        showToast(
          usedRemoteFallback
            ? '✓ Hero image generated (via Supabase — restart dev server for local composer)'
            : (result.message ?? '✓ Hero image generated'),
        );
      } else {
        throw new Error(result.message ?? 'Hero image was not generated.');
      }
    } catch (err) {
      if (previousFeatured) {
        setMeta((m) => ({ ...m, featured_image: previousFeatured }));
      }
      setSaveError((err as Error).message);
    } finally {
      setGeneratingImages(false);
    }
  }, [savedPostId, meta.slug, meta.title, meta.keywords, meta.meta_description, meta.featured_image, hero.headline, sections]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
             style={{ borderColor: 'var(--ds-brand)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // Build mock BlogSection[] for the preview renderer
  const previewSections: BlogSection[] = sections.map((s, i) => ({
    id: s._id,
    post_id: savedPostId ?? '',
    position: i,
    type: s.type,
    heading: s.heading || null,
    data: s.data,
    created_at: new Date().toISOString(),
  }));

  // Determine if a TOC sidebar layout should be shown in the preview
  const previewTocSection = previewSections.find((s) => s.type === 'toc');
  const previewTocAlign = previewTocSection
    ? ((previewTocSection.data as { align?: string }).align ?? 'top')
    : 'top';
  const previewHasSidebar = previewTocAlign === 'left' || previewTocAlign === 'right';
  const previewBodySections = placeMidSubscribeCta(
    previewHasSidebar
      ? previewSections.filter((s) => s.type !== 'toc')
      : previewSections,
  );

  const navigateBack = () => {
    window.history.pushState({}, '', '/admin/blogs');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className={`${embedded ? 'h-full flex flex-col' : 'min-h-screen'} ds-page`}>
      {/* Inline action bar — back button + preview toggle + status bar */}
      <div className="sticky top-0 z-20 border-b px-3 sm:px-4 py-2.5 backdrop-blur"
        style={{ background: 'var(--ds-bg-card)', borderColor: 'var(--ds-border)' }}>
        <div className="flex flex-col gap-2 sm:gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={navigateBack}
              className="ds-btn ds-btn-ghost ds-btn-sm min-h-[44px] min-w-[44px] touch-manipulation shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <div className="flex-1 min-w-0 truncate">
              <span className="t-card-title">
                {isNew ? 'New post' : meta.title || 'Untitled'}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex lg:hidden rounded-lg border p-0.5 gap-0.5 min-w-0"
              style={{ borderColor: 'var(--ds-border)' }}>
              <button
                type="button"
                onClick={() => setMobilePane('editor')}
                className={`flex-1 min-h-[44px] rounded-md text-sm font-medium touch-manipulation transition-colors ${
                  mobilePane === 'editor' ? 'ds-btn-primary' : 'ds-btn-ghost'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-1.5" />
                Editor
              </button>
              <button
                type="button"
                onClick={() => setMobilePane('preview')}
                className={`flex-1 min-h-[44px] rounded-md text-sm font-medium touch-manipulation transition-colors ${
                  mobilePane === 'preview' ? 'ds-btn-primary' : 'ds-btn-ghost'
                }`}
              >
                <Eye className="w-4 h-4 inline mr-1.5" />
                Preview
              </button>
            </div>

            <StatusBar
              status={currentStatus}
              saving={saving}
              onSave={handleSave}
              scheduledFor={scheduledFor}
              onScheduledForChange={setScheduledFor}
            />
          </div>
        </div>
      </div>

      {/* Two-column layout: Editor | Preview (stacked + tabbed on mobile) */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 min-w-0">
        {/* LEFT — Editor */}
        <div
          className={`w-full lg:w-1/2 border-b lg:border-b-0 lg:border-r overflow-y-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col gap-7 min-w-0 ${
            mobilePane === 'preview' ? 'hidden lg:flex' : 'flex'
          }`}
          style={{ borderColor: 'var(--ds-border)' }}
        >
          {saveError && (
            <div className="ds-recessed-danger t-meta">
              {saveError}
            </div>
          )}

          <section>
            <SectionHeader icon={<FileText className="w-4 h-4" />} title="Post details" />
            <div className="mt-4">
              <BlogMetaForm
                values={meta}
                postId={savedPostId}
                onChange={(patch) => setMeta((m) => ({ ...m, ...patch }))}
                onGenerateImages={handleGenerateImages}
                generatingImages={generatingImages}
              />
            </div>
          </section>

          <section>
            <SectionHeader icon={<FileText className="w-4 h-4" />} title="Hero block" />
            <div className="mt-4">
              <HeroEditor
                values={hero}
                onChange={(patch) => setHero((h) => ({ ...h, ...patch }))}
              />
            </div>
          </section>

          <section>
            <SectionHeader icon={<FileText className="w-4 h-4" />} title="Content sections" />
            <p className="t-meta mt-1 mb-4">
              Drag the grip handle to reorder. Position is re-computed on every save.
            </p>
            <SectionsList sections={sections} onChange={setSections} />
          </section>
        </div>

        {/* RIGHT — Live preview */}
        <div
          className={`w-full lg:w-1/2 overflow-y-auto lg:sticky lg:top-[53px] lg:h-[calc(100vh-53px)] min-w-0 ${
            mobilePane === 'editor' ? 'hidden lg:block' : 'block'
          }`}
          style={{ background: 'var(--ds-bg-tinted)' }}
        >
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
              <div className="t-section mb-6 flex items-center gap-2"
                   style={{ color: 'var(--ds-brand)' }}>
                <Eye className="w-3.5 h-3.5" /> Live Preview
              </div>

              {/* Hero preview */}
              {meta.featured_image && (
                <img
                  src={resolveBlogImageSrc(meta.featured_image, meta.slug) ?? meta.featured_image}
                  alt="Featured"
                  className="w-full h-48 object-cover rounded-xl mb-6"
                />
              )}
              {meta.title && (
                <h1 className="t-page-title mb-2">{meta.title}</h1>
              )}
              <div className="flex items-center gap-3 t-meta mb-4">
                {meta.author_name && <span>{meta.author_name}</span>}
                {meta.category && (
                  <span className="ds-chip">
                    {meta.category}
                  </span>
                )}
                {meta.read_time_min && <span>{meta.read_time_min} min read</span>}
              </div>
              {meta.meta_description && (
                <p className="t-meta mb-6 italic">{meta.meta_description}</p>
              )}

              {/* Hero headline + intro */}
              {(hero.headline || hero.intro) && (
                <div className="ds-card-sm mb-8">
                  {hero.headline && (
                    <h2 className="t-h3 mb-2">{hero.headline}</h2>
                  )}
                  {hero.intro && (
                    <div className="t-meta prose prose-sm max-w-none dark:prose-invert">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{hero.intro}</ReactMarkdown>
                    </div>
                  )}
                </div>
              )}

              {/* Sections preview */}
              {previewSections.length > 0 ? (() => {
                // Social strip preview — coloured brand icons, no labels, no Twitter.
                const PREVIEW_SOCIALS = [
                  { Icon: Linkedin,  color: 'var(--ds-info)', label: 'LinkedIn'  },
                  { Icon: Instagram, color: 'var(--ds-danger)', label: 'Instagram' },
                  { Icon: Facebook,  color: 'var(--ds-brand)', label: 'Facebook'  },
                  { Icon: Youtube,   color: 'var(--ds-danger)', label: 'YouTube'   },
                ] as const;

                const SocialStripVertical = (
                  <div className="shrink-0 flex flex-col items-center gap-2 pt-1">
                    {PREVIEW_SOCIALS.map(({ Icon, color, label }) => (
                      <div
                        key={label}
                        title={label}
                        className="flex items-center justify-center w-8 h-8 rounded-full text-white"
                        style={{ background: color }}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                    ))}
                    <div className="w-4 border-t border-white/20 my-0.5" />
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white"
                      style={{ background: 'var(--ds-brand)' }}
                    >
                      <Copy className="w-4 h-4" />
                    </div>
                  </div>
                );

                const SocialStripHorizontal = (
                  <div className="flex items-center gap-2 flex-wrap pb-4 border-b mb-2"
                    style={{ borderColor: 'var(--ds-border)' }}>
                    {PREVIEW_SOCIALS.map(({ Icon, color, label }) => (
                      <div
                        key={label}
                        title={label}
                        className="flex items-center justify-center w-8 h-8 rounded-full text-white"
                        style={{ background: color }}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                    ))}
                    <div className="w-px h-6 border-l mx-1" style={{ borderColor: 'var(--ds-border)' }} />
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white"
                      style={{ background: 'var(--ds-brand)' }}
                    >
                      <Copy className="w-4 h-4" />
                    </div>
                  </div>
                );

                if (previewHasSidebar) {
                  return (
                    /* Sidebar layout — social strip + TOC + body */
                    <div className={`flex gap-3 items-start ${previewTocAlign === 'right' ? 'flex-row-reverse' : 'flex-row'}`}>

                      {/* ── Social strip (vertical column) ── */}
                      {SocialStripVertical}

                      {/* ── TOC sidebar (clickable items) ── */}
                      <div className="w-36 shrink-0 sticky top-4">
                        <div className="ds-card-sm">
                          <p className="t-label mb-2">
                            {previewTocSection?.heading || 'In this article'}
                          </p>
                          <ol className="list-none m-0 p-0 space-y-0">
                            {((previewTocSection?.data as { items?: string[] })?.items ?? []).map((item, i) => (
                              <li key={i} className="flex items-start gap-1.5 py-1.5 border-b last:border-b-0"
                                style={{ borderColor: 'var(--ds-border)' }}>
                                <span className="t-tag t-muted t-num w-3 text-right shrink-0 mt-px">{i + 1}.</span>
                                <a
                                  href={`#preview-section-${i}`}
                                  onClick={(e) => {
                                    const el = document.getElementById(`preview-section-${i}`);
                                    if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
                                  }}
                                  className="t-tag t-link leading-snug no-underline transition-colors"
                                >
                                  {item}
                                </a>
                              </li>
                            ))}
                          </ol>
                        </div>
                        <div className="mt-1.5 text-center">
                          <span className="ds-chip">
                            {previewTocAlign === 'left' ? '← Left sidebar' : 'Right sidebar →'}
                          </span>
                        </div>
                      </div>

                      {/* ── Body (sections with scroll target IDs) ── */}
                      <div className="flex-1 min-w-0 space-y-6">
                        {previewBodySections.map((s, i) => (
                          <div key={s.id} id={`preview-section-${i}`} className="scroll-mt-4">
                            <SectionRenderer section={s} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Inline layout — social strip at TOP (always visible), then sections
                return (
                  <>
                    {SocialStripHorizontal}
                    <div className="space-y-6">
                      {previewSections.map((s, i) => {
                        const sIdx = s.type !== 'toc' ? i : undefined;
                        return (
                          <div key={s.id} id={sIdx !== undefined ? `preview-section-${sIdx}` : undefined} className="scroll-mt-4">
                            <SectionRenderer section={s} />
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })() : (
                <div className="ds-card text-center py-12">
                  <p className="t-h3">No preview yet</p>
                  <p className="t-meta mt-1">Add sections on the left to see them previewed here.</p>
                </div>
              )}

              {/* Keywords */}
              {meta.keywords.length > 0 && (
                <div className="mt-8 flex flex-wrap gap-2">
                  {meta.keywords.map((kw) => (
                    <span key={kw} className="ds-chip ds-chip-brand">
                      {kw}
                    </span>
                  ))}
                </div>
              )}
            </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="ds-card-pop fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          {toast}
        </div>
      )}
    </div>
  );
};

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div className="flex items-center gap-2 pb-2 border-b" style={{ borderColor: 'var(--ds-border)' }}>
    <span className="t-muted">{icon}</span>
    <h2 className="t-section">{title}</h2>
  </div>
);

// ── Outer guard (Supabase session + editor role) ──────────────────────────────

const BlogEditorPage: React.FC<Props> = (props) => {
  const { session, loading } = useAuthentication();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
             style={{ borderColor: 'var(--ds-brand)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="ds-card text-center max-w-sm">
          <h2 className="t-h3 mb-2">Sign in required</h2>
          <p className="t-meta mb-4">Sign in with Google or LinkedIn to use the blog editor.</p>
          <a href="/login" className="ds-btn ds-btn-primary">
            Go to sign in
          </a>
        </div>
      </div>
    );
  }

  const role = getJwtUserRole(session);
  if (!role) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="ds-card text-center max-w-sm">
          <h2 className="t-h3 mb-2">Access not granted</h2>
          <p className="t-meta">Your account needs <code className="ds-chip ds-chip-warn">editor</code> or <code className="ds-chip ds-chip-warn">admin</code> role.</p>
        </div>
      </div>
    );
  }

  return <BlogEditorInner {...props} />;
};

export default BlogEditorPage;
